/* eslint-disable no-underscore-dangle */
import './dispute.d';
import {
  get,
  post,
} from '../http';
import {
  clearDisputeJourneyActive,
  markDisputeJourneyActive,
} from '../journeyState';

/**
 * An API layer for COAC API requests
 */
export default class Dispute {
  /**
   * @param {String} api - the fully qualified domain to issue requests against
   */
  constructor({
    api = null,
  }) {
    /**
     * The fully qualified base url for the MGO API
     * @type {String}
     */
    this.api = api;

    /**
     * COA order confirmation code
     * Obtained through login with Validation letter key/zip combo
     * @type {String}
     */
    this.cfpsId = sessionStorage.getItem('coaccfpsid') || '';

    /**
     * 13 digit key from COA Validation Letter
     * @type {String}
     */
    this.coaKey = sessionStorage.getItem('coackey') || '';

    /**
     * ZIP code on the COA Validation Letter
     * @type {String}
     */
    this.loginZip = sessionStorage.getItem('coaccode') || '';

    /**
     * The Visit Id of the current session
     * @type {Number}
     */
    this.visitId = null;
  }

  reset() {
    this.cfpsId = '';
    this.coaKey = '';
    this.loginZip = '';
    this.visitId = null;
    clearDisputeJourneyActive();
    sessionStorage.removeItem('coaccfpsid');
    sessionStorage.removeItem('coackey');
    sessionStorage.removeItem('coaccode');
    sessionStorage.removeItem('moverType');
  }

  /**
   * Retrieve Application Configuration Details
   * These config items relate to Command Center
   * @method configGet
   * @return {Promise<Config>}
   */
  async configGet() {
    return get(`${this.api}/mgo-api/api/v1/app/config`, {}, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }

  /**
   * COA Dispute Actions
   * ----------------------------------------------------------------
   */

  /**
   * Login to existing COA order
   * @param {String} coaKey - 13 digit key from COA Validation Letter
   * @param {String} zip5 - ZIP code on the COA Validation Letter
   * @return {Promise<String>}
   */
  async login(coaKey, zip5) {
    clearDisputeJourneyActive();
    const {
      cfpsId,
      moverType,
      newZip,
      raw,
    } = await get(`${this.api}/mgo-api/api/v1/dispute?coaKey=${coaKey}&oldZip=${zip5}`, {}, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });

    if (raw) {
      return Promise.resolve(raw.data || { exception: 'RequestFailed' });
    }

    this.cfpsId = cfpsId;
    this.coaKey = coaKey;
    this.loginZip = zip5;
    this.newZip = newZip;

    sessionStorage.setItem('coaccfpsid', cfpsId);
    sessionStorage.setItem('coackey', coaKey);
    sessionStorage.setItem('coaccode', zip5);
    sessionStorage.setItem('moverType', moverType);
    markDisputeJourneyActive();

    return Promise.resolve('');
  }

  /**
   * Search for existing COA data
   * @return {Promise<DisputeSearchResponse>}
   */
  async search() {
    try {
      return get(`${this.api}/mgo-api/api/v1/dispute?coaKey=${this.coaKey}&oldZip=${this.loginZip}`, {}, {
        headers: {
          Authorization: `Bearer ${this.token}`,
        },
      });
    } catch (err) {
      return Promise.resolve(err);
    }
  }

  /**
   * Submit a dispute for the COA order
   * @param {String} firstName
   * @param {String} lastName
   * @param {String} email
   * @param {String} phone
   * @param {String} preferredContactMethod - EMAIL | PHONE
   * @param {String} suffix
   * @param {String} title
   * @param {String} streetAddress
   * @param {String} city
   * @param {String} state
   * @param {String} zipCode
   * @param {Boolean} addressee
   * @param {Boolean} filedCoa
   * @param {Boolean} jointAddressee
   * @param {Boolean} nameRecognition
   * @param {Boolean} newResident
   * @param {Boolean} previousResident
   * @param {Boolean} remainAtAddress
   * @return {Promise<String>}
   */
  async submit({
    firstName,
    lastName,
    moverType,
    email,
    phone,
    preferredContactMethod,
    suffix = null,
    title = null,
    streetAddress,
    city,
    state,
    addressee = null,
    filedCoa = null,
    jointAddressee = null,
    nameRecognition = null,
    newResident = null,
    previousResident = null,
    remainAtAddress = null,
  }) {
    let url = 'dispute/partial-dispute';

    if (firstName && lastName) {
      url = 'dispute';
    }
    return post(`${this.api}/mgo-api/api/v1/${url}`, {
      cfpsId: this.cfpsId,
      coaKey: this.coaKey,
      loginZip: this.loginZip,
      newZipCode: this.newZip,
      firstName,
      lastName,
      moverType,
      email,
      phone,
      preferredContactMethod,
      suffix,
      title,
      streetAddress,
      city: city?.replace(/[-.,"]/g, ''),
      state,
      zipCode: this.loginZip,
      addressee,
      filedCoa,
      jointAddressee,
      nameRecognition,
      newResident,
      previousResident,
      remainAtAddress,
    }, {
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
  }


  /**
   * Analytics Events
   * ----------------------------------------------------------------
   */

  /**
   * Get the visitId
   * @param {String} deviceType - DESKTOP | MOBILE
   * @param {String} queryString - url query string
   * @return {Promise<Number>}
   */
  async visit(deviceType, queryString) {
    const visitId = await post(
      `${this.api}/mgo-api/api/v1/e/v`,
      {
        params: {
          qs: queryString,
          deviceType,
        },
      },
      {
        headers: {
          Authorization: `Bearer ${this.token}`,
        },
      },
    );

    this.visitId = visitId;

    return Promise.resolve(visitId);
  }
}
